import { Component, computed, effect, inject, Signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService, Translation } from '@ngx-translate/core';

import { LanguageService } from '@core/i18n/language.service';
import { ResponseStatusService } from '@core/ssr/response-status.service';
import { SeoService } from '@core/seo/seo.service';

/** HTTP status sent for a page that does not exist. */
const NOT_FOUND_STATUS: number = 404;

/** i18n key shared by the visible heading and the document title, so the two never disagree. */
const TITLE_KEY: string = 'core.notFound.title';

/**
 * The 404 page — rendered both by the catch-all route and inline by `DynamicPage` when a slug
 * resolves to no CMS article, so a missing page looks and behaves the same either way.
 *
 * Constructing it is what makes the response a *real* 404: it sets the SSR status and marks the page
 * `noindex, follow`, so a crawler sees a missing page instead of a 200 soft 404 while still walking
 * the links in the header/footer.
 */
@Component({
    selector: 'app-not-found',
    imports: [RouterLink, MatButtonModule, TranslatePipe],
    templateUrl: './not-found.html',
    styleUrl: './not-found.scss',
})
export class NotFound {
    private readonly _responseStatus: ResponseStatusService = inject(ResponseStatusService);
    private readonly _seo: SeoService = inject(SeoService);
    private readonly _language: LanguageService = inject(LanguageService);
    private readonly _translate: TranslateService = inject(TranslateService);

    /** Route to the home page of the active language. */
    protected readonly homeLink: Signal<Array<string>> = computed((): Array<string> => [
        '/',
        this._language.lang(),
    ]);

    /** Translated document title; `stream` re-emits when the switcher changes language. */
    private readonly _title: Signal<Translation> = toSignal(this._translate.stream(TITLE_KEY), {
        initialValue: '',
    });

    constructor() {
        this._responseStatus.set(NOT_FOUND_STATUS);

        // Re-applied per language so the tab says "Page not found" rather than the marketing
        // tagline. `noindex, follow` keeps the missing page out of the index while a crawler still
        // walks the header/footer links on it.
        effect((): void => {
            this._seo.setSeo({
                title: (this._title() as string) || null,
                robots: 'noindex, follow',
            });
        });
    }
}
